import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaUserMd,
  FaCalendarAlt,
  FaClock,
  FaHospital,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
} from 'react-icons/fa';
import axios from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import BottomNav from '../../components/BottomNav';

interface Doctor {
  id: number;
  name: string;
  avatar_url: string;
  rating: string;
  his_doctor_id: string;
  specialities: { code: string; name: string }[];
  organizations: { code: string; name: string }[];
  departemens?: {
    practitioner_role_id: number;
    departemen_name: string;
    departemen_id: string;
  }[];
}

interface LocationState {
  doctor: Doctor;
  selectedDate: string;
  selectedTime: string;
  timezone: string;
  teleconsultId?: number;
  polyName?: string;
  polyCode?: string;
  jenisPenjamin?: string;
  noKartu?: string;
  noRujukan?: string;
}

type BookingStatus = 'idle' | 'loading' | 'success' | 'error';

const BookingConfirm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const { token: authToken, user: userData } = useAuth();

  const { referenceId } = useParams<{ referenceId: string }>();
  const { doctor, selectedDate, selectedTime, timezone, polyCode, polyName } = state;

  const [bookingStatus, setBookingStatus] = useState<BookingStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [bookingResult, setBookingResult] = useState<any>(null);

  if (!doctor || !selectedDate || !selectedTime) {
    navigate(-1);
    return null;
  }

  const speciality  = doctor.specialities?.[0]?.name || 'Dokter';
  const hospital    = doctor.organizations?.[0]?.name || '';
  const departemen  = doctor.departemens?.find(d => d.departemen_id === polyCode) ?? doctor.departemens?.[0];

  const formattedDate = new Date(selectedDate + 'T00:00:00').toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const handleBooking = async () => {
    setBookingStatus('loading');
    setErrorMessage('');

    try {
      const specialityCode = doctor.specialities?.[0]?.code || '';
      const specialityName = doctor.specialities?.[0]?.name || '';

      const payload = {
        teleconsult_id:  state.teleconsultId,
        reference_id:    referenceId ? Number(referenceId) : undefined,
        practitioner_id: doctor.id,
        kode_dep:        departemen?.departemen_id,
        kode_dokter:     doctor.his_doctor_id,
        doctor_name:     doctor.name,
        poly_name:       polyName || departemen?.departemen_name,
        speciality_code:  specialityCode,
        speciality_name:  specialityName,
        reference_date:  selectedDate,
        jam:             selectedTime,
        timezone:        timezone,
        no_kartu:        state.noKartu || '',
        no_rujukan:      state.noRujukan || '',
      };

      const res = await axios.post('reference/booking', payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      setBookingResult(res.data);
      setBookingStatus('success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        'Terjadi kesalahan. Silakan coba beberapa saat lagi.';
      setErrorMessage(msg);
      setBookingStatus('error');
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (bookingStatus === 'success') {
    const lmsData = bookingResult?.lms_response?.data?.[0] ?? {};
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-24">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 w-full max-w-sm p-8 text-center">
            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheckCircle className="text-teal-500" size={40} />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Booking Berhasil!</h2>
            <p className="text-sm text-gray-500 mb-6">
              Jadwal Anda telah dikonfirmasi
            </p>

            {/* Kode booking */}
            {lmsData.KodeBooking && (
              <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 mb-5">
                <p className="text-xs text-gray-500 mb-1">Kode Booking</p>
                <p className="text-2xl font-bold text-teal-600 tracking-widest">
                  {lmsData.KodeBooking}
                </p>
              </div>
            )}

            {/* Detail ringkas */}
            <div className="space-y-3 text-left">
              {[
                { label: 'Dokter',   value: lmsData.Dokter || doctor.name },
                { label: 'Poli',     value: lmsData.Poli || polyName },
                { label: 'Tanggal', value: lmsData.Tanggal ? new Date(lmsData.Tanggal + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : formattedDate },
                { label: 'Jam',      value: `${lmsData.Jam || selectedTime} ${timezone}` },
                { label: 'Penjamin', value: lmsData.Jenis || state.jenisPenjamin || 'PERUSAHAAN' },
              ].map(item => (
                <div key={item.label} className="flex justify-between text-sm">
                  <span className="text-gray-400">{item.label}</span>
                  <span className="font-medium text-gray-700 text-right max-w-[60%]">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-2">
              <button
                onClick={() => navigate('/reference', { replace: true })}
                className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-2xl text-sm transition-colors"
              >
                Kembali ke Daftar Rujukan
              </button>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold rounded-2xl text-sm transition-colors"
              >
                Ke Beranda
              </button>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // ── Main confirm screen ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-teal-600 text-white px-4 pt-4 pb-5 shadow-md">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 p-1 rounded-full hover:bg-teal-500 transition"
            disabled={bookingStatus === 'loading'}
          >
            <FaArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg font-bold">Konfirmasi Booking</h1>
            <p className="text-teal-100 text-xs">Periksa kembali sebelum melanjutkan</p>
          </div>
        </div>
      </div>

      <div className="flex-1 pb-32 overflow-y-auto px-4 pt-4 space-y-4">

        {/* Error banner */}
        {bookingStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                <FaTimesCircle className="text-red-400 shrink-0 mt-0.5" size={18} />
                <div>
                    <p className="text-sm font-semibold text-red-700 mb-0.5">Booking Gagal</p>
                    <p className="text-sm text-red-600">{errorMessage}</p>
                </div>
                </div>

                {/* tombol silang */}
                <button
                onClick={() => setBookingStatus('idle')}
                className="text-red-400 hover:text-red-600"
                aria-label="Tutup"
                >
                ✕
                </button>
            </div>
        )}

        {/* Doctor card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 pt-3 pb-1 border-b border-gray-50">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Dokter</p>
          </div>
          <div className="flex items-center gap-3 p-4">
            <img
              src={doctor.avatar_url}
              alt={doctor.name}
              onError={e => {
                (e.target as HTMLImageElement).src =
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=0d9488&color=fff&size=64`;
              }}
              className="w-14 h-14 rounded-xl object-cover bg-teal-50 shrink-0"
            />
            <div>
              <h2 className="font-semibold text-gray-800 text-sm">{doctor.name}</h2>
              <span className="inline-block mt-1 text-xs bg-teal-50 text-teal-700 font-medium px-2 py-0.5 rounded-full">
                {speciality}
              </span>
              {hospital && (
                <div className="flex items-center gap-1 mt-1">
                  <FaHospital className="text-gray-300" size={11} />
                  <p className="text-xs text-gray-400">{hospital}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Jadwal card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 pt-3 pb-1 border-b border-gray-50">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Jadwal</p>
          </div>
          <div className="p-4 space-y-3">
            <DetailRow
              icon={<FaCalendarAlt className="text-teal-400" size={14} />}
              label="Tanggal"
              value={formattedDate}
            />
            <DetailRow
              icon={<FaClock className="text-teal-400" size={14} />}
              label="Jam"
              value={`${selectedTime} ${timezone}`}
            />
            <DetailRow
              icon={<FaUserMd className="text-teal-400" size={14} />}
              label="Poli"
              value={polyName || departemen?.departemen_name || '-'}
            />
          </div>
        </div>

        {/* Warning notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-start gap-2.5">
          <FaExclamationTriangle className="text-amber-400 shrink-0 mt-0.5" size={14} />
          <p className="text-xs text-amber-700 leading-relaxed">
            Pastikan data sudah benar sebelum melakukan booking. Pembatalan mungkin memerlukan konfirmasi dari rumah sakit.
          </p>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="fixed bottom-16 left-0 right-0 px-4 pb-3 pt-4 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent">
        <button
          disabled={bookingStatus === 'loading'}
          onClick={handleBooking}
          className={`w-full py-3.5 rounded-2xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            bookingStatus === 'loading'
              ? 'bg-teal-300 text-white cursor-not-allowed'
              : 'bg-teal-500 hover:bg-teal-600 text-white '
          }`}
        >
          {bookingStatus === 'loading' ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sedang Memproses...
            </>
          ) : (
            'Konfirmasi Booking'
          )}
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

// ── Helper component ──────────────────────────────────────────────────────────
const DetailRow: React.FC<{
  icon?: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2 text-gray-400 text-sm shrink-0">
      {icon}
      <span>{label}</span>
    </div>
    <span className="text-sm font-medium text-gray-700 text-right">{value}</span>
  </div>
);

export default BookingConfirm;